import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  fetchConnectedInstagramIdentity,
  isCommentFromConnectedAccount,
} from "@/lib/instagram-comment-filters";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { InstagramPostThumbnail } from "@/components/conversations/InstagramPostThumbnail";
import { Instagram } from "lucide-react";
import {
  resolveInstagramPostThumbnailUrls,
  type InstagramPostThumbnailFields,
} from "@/lib/instagram-post-thumbnail";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface InstagramComment {
  id: string;
  text: string;
  from_user_id: string;
  from_username: string | null;
  is_replied: boolean;
  reply_text: string | null;
  created_at: string;
  post: {
    id: string;
    thumbnail_url: string | null;
    storage_thumbnail_url: string | null;
    storage_thumbnail_object_key: string | null;
    media_url: string | null;
    caption: string | null;
    permalink: string | null;
  } | null;
}

interface LeadInstagramCommentsProps {
  leadId: string;
  workspaceId: string;
}

export function LeadInstagramComments({ leadId, workspaceId }: LeadInstagramCommentsProps) {
  const [comments, setComments] = useState<InstagramComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchComments();
  }, [leadId, workspaceId]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const identity = await fetchConnectedInstagramIdentity(supabase, workspaceId);

      const { data, error } = await supabase
        .from("instagram_comments")
        .select(`
          id,
          text,
          from_user_id,
          from_username,
          is_replied,
          reply_text,
          created_at,
          post:post_id (
            id,
            thumbnail_url,
            storage_thumbnail_url,
            storage_thumbnail_object_key,
            media_url,
            caption,
            permalink
          )
        `)
        .eq("lead_id", leadId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      const rows = (data as InstagramComment[]) || [];
      const filtered = rows.filter((c) => !isCommentFromConnectedAccount(c, identity));
      setComments(filtered);

      const posts = filtered
        .map((c) => c.post)
        .filter((post): post is NonNullable<InstagramComment["post"]> => post != null)
        .map((post) => post as InstagramPostThumbnailFields);
      const uniquePosts = [...new Map(posts.map((post) => [post.id, post])).values()];
      const presigned = await resolveInstagramPostThumbnailUrls(workspaceId, uniquePosts);
      setThumbnailUrls(presigned);
    } catch (error) {
      console.error("Error fetching lead comments:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Instagram className="w-4 h-4 text-pink-500" />
        <h3 className="font-semibold text-sm">Comentários do Instagram</h3>
        <Badge variant="outline">{comments.length}</Badge>
      </div>

      {comments.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum comentário</p>
      ) : (
        <div className="space-y-3 max-h-60 overflow-y-auto">
          {comments.map((comment) => (
            <div key={comment.id} className="border rounded-lg p-3">
              <div className="flex gap-2">
                {comment.post ? (
                  <InstagramPostThumbnail
                    post={comment.post}
                    presignedByPostId={thumbnailUrls}
                    size="sm"
                  />
                ) : null}
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{comment.text}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {format(new Date(comment.created_at), "dd/MM/yyyy HH:mm", {
                      locale: ptBR,
                    })}
                  </p>
                  {comment.is_replied && comment.reply_text && (
                    <div className="mt-2 pl-3 border-l-2 border-primary/30">
                      <p className="text-sm text-primary">{comment.reply_text}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
